import { useState, useEffect, useCallback } from 'react';
import { DollarSign, Users, CalendarClock, AlertTriangle, FileText, X } from 'lucide-react';
import apiService from '../services/apiService';
import ActionableItemCard from '../components/AIM/ActionableItemCard';
import { theme, rgba } from '../theme';
import { formatCurrency } from '../utils/helpers';

// Clicking a domain swaps which backend assembler ran to produce `items` - see
// GET /api/aim/:domain - not a client-side filter over one pre-fetched flat list.
const DOMAIN_META = {
  finance:    { label: 'Finance',    icon: DollarSign },
  employees:  { label: 'Employees',  icon: Users },
  scheduling: { label: 'Scheduling', icon: CalendarClock },
  operations: { label: 'Operations', icon: AlertTriangle },
  reports:    { label: 'Reports',    icon: FileText },
};
export default function AIMPage({ onOpenChat, initialDomain }) {
  const [activeDomain, setActiveDomain] = useState(null);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [scopeType, setScopeType] = useState('company');
  const [scopeId, setScopeId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [domainLoading, setDomainLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [financeSnapshot, setFinanceSnapshot] = useState(null);

  // Resolve this user's real allowed-domains list (access control, enforced server-side
  // on every /api/aim/:domain request - this only decides which tabs to render) plus
  // their role-derived-or-saved default - GET /api/aim/defaults falls back
  // adminOverride -> userPreference -> jobTitle -> ROLE_RANK.
  useEffect(() => {
    (async () => {
      try {
        const data = await apiService.getAimDefaults();
        setAllowedDomains(data.allowedDomains || []);
        // A dashboard tile click can request a specific domain (e.g. Net Profit ->
        // finance) - only honored if this user's real access actually includes it
        // (data.allowedDomains, resolved server-side); otherwise falls back to their
        // normal saved/role-derived default, same as no deep link at all. This is a
        // one-time landing override, not a preference change - only a manual tab
        // switch afterward (switchDomain below) updates their saved default.
        const landingDomain = (initialDomain && (data.allowedDomains || []).includes(initialDomain))
          ? initialDomain
          : data.domain;
        setActiveDomain(landingDomain);
        setScopeType(data.scopeType || 'company');
        setScopeId(data.scopeId || null);
      } catch (err) {
        setError(err.message || 'Failed to load AIM access.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadItems = useCallback(async () => {
    if (!activeDomain) return;
    setDomainLoading(true);
    setError(null);
    try {
      const data = await apiService.getAimDomain(activeDomain, scopeType, scopeId);
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load items.');
    } finally {
      setDomainLoading(false);
    }
  }, [activeDomain, scopeType, scopeId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Traced live 2026-09-16: Finance domain is genuinely an alerts feed (unconfirmed
  // rates, unstaffed contracts, negative/thin margins, ambiguous overhead), not a
  // financial-breakdown view - confirmed via GET /api/aim/finance returning items: []
  // for a company with a healthy 58.1% margin and no open issues, while every other
  // domain (scheduling, employees, reports) had real items at the same time. Nothing
  // was broken in the fetch/deep-link path itself. But a dashboard tile promising "the
  // relevant financial breakdown" landing on a bare "Nothing needs attention here right
  // now" - with the real, healthy numbers nowhere in sight - reads as broken even though
  // it isn't. Reuses apiService.getBusinessData() (the same call Dashboard itself
  // makes, same businessService.getFinancialAnalysis source) purely to make the empty
  // state honest and useful, not to turn this into a persistent dashboard - every other
  // domain's empty state is untouched.
  useEffect(() => {
    if (activeDomain !== 'finance' || domainLoading || items.length > 0) return;
    let cancelled = false;
    apiService.getBusinessData()
      .then(data => {
        if (!cancelled && data?.success) setFinanceSnapshot(data.data?.financialAnalysis || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeDomain, domainLoading, items.length]);

  const switchDomain = (domain) => {
    if (domain === activeDomain) return;
    setActiveDomain(domain);
    // Whichever domain someone actually lands on becomes their new default - that's the
    // whole point of a per-user override existing (models/User.js's dashboardPreferences).
    apiService.updateAimPreferences({ aimDomain: domain }).catch(() => {});
  };

  const clearScope = () => {
    setScopeType('company');
    setScopeId(null);
    apiService.updateAimPreferences({ aimScopeType: 'company', aimScopeId: null }).catch(() => {});
  };

  const handleAcknowledge = async (id) => {
    if (!id) return;
    setBusyId(id);
    try {
      await apiService.acknowledgeOperationalAlert(id);
      await loadItems();
    } catch (err) {
      setError(err.message || 'Failed to acknowledge.');
    } finally {
      setBusyId(null);
    }
  };

  const handleResolve = async (id) => {
    if (!id) return;
    setBusyId(id);
    try {
      await apiService.resolveOperationalAlert(id);
      await loadItems();
    } catch (err) {
      setError(err.message || 'Failed to resolve.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div style={styles.page}><p style={{ color: '#94a3b8' }}>Loading...</p></div>;
  }

  if (allowedDomains.length === 0) {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Actionable Items</h1>
        <p style={{ color: '#64748b', marginTop: 16 }}>You don't have access to any AIM domain yet - ask an admin if you think this is wrong.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Actionable Items</h1>
          <p style={styles.sub}>What needs your attention, by domain</p>
        </div>
        {scopeType !== 'company' && (
          <div style={styles.scopeChip}>
            {scopeType}: {scopeId || '(unspecified)'}
            <button onClick={clearScope} style={styles.scopeClearBtn} title="Back to company-wide">
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      <div style={styles.tabRow}>
        {allowedDomains.map(domain => {
          const meta = DOMAIN_META[domain];
          if (!meta) return null;
          const Icon = meta.icon;
          const isActive = activeDomain === domain;
          return (
            <button
              key={domain}
              onClick={() => switchDomain(domain)}
              style={{ ...styles.tabBtn, ...(isActive ? styles.tabBtnActive : {}) }}
            >
              <Icon size={16} /> {meta.label}
            </button>
          );
        })}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {domainLoading ? (
        <p style={{ color: '#94a3b8' }}>Loading...</p>
      ) : items.length === 0 ? (
        activeDomain === 'finance' && financeSnapshot ? (
          // Same real item shape every other AIM card uses (aimItemShape.js), not a
          // dashboard tile transplanted in - just a client-built "healthy" item instead
          // of one a backend assembler emitted, rendered through the exact same
          // ActionableItemCard component so it's pixel-identical to a real one.
          <ActionableItemCard
            item={{
              domain: 'finance',
              severity: 'healthy',
              title: 'Finances are healthy this month',
              description: financeSnapshot.netProfit != null
                ? `Net profit is ${formatCurrency(financeSnapshot.netProfit)} on ${formatCurrency(financeSnapshot.totalRevenue)} in revenue${financeSnapshot.netProfitMargin != null ? ` — a ${financeSnapshot.netProfitMargin.toFixed(1)}% margin` : ''}. No unconfirmed pay rates, no unstaffed contracts, and no client running thin or negative. Nothing here needs a decision right now.`
                : `Revenue is ${formatCurrency(financeSnapshot.totalRevenue)} this month. No unconfirmed pay rates, no unstaffed contracts, and no client running thin or negative.`,
              sourceType: 'finance_healthy',
              entityRef: null,
              suggestedAction: 'Give me the full financial breakdown',
              kind: 'derived',
            }}
            onOpenChat={onOpenChat}
          />
        ) : (
          <p style={{ color: '#64748b', textAlign: 'center', padding: 32 }}>Nothing needs attention here right now.</p>
        )
      ) : (
        <div style={styles.list}>
          {items.map((item, i) => (
            <ActionableItemCard
              key={item.entityRef ? `${item.entityRef.type}-${item.entityRef.id}-${item.sourceType}` : `${item.sourceType}-${i}`}
              item={item}
              onOpenChat={onOpenChat}
              onAcknowledge={handleAcknowledge}
              onResolve={handleResolve}
              onAfterAction={loadItems}
              busy={busyId === item.entityRef?.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  page:      { padding: '32px', minHeight: '100vh', background: `linear-gradient(135deg,${rgba(theme.colors.primaryRgb, 0.05)},${rgba(theme.colors.accentRgb, 0.05)})` },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title:     { color: '#e2e8f0', fontSize: '28px', fontWeight: '700', margin: 0 },
  sub:       { color: theme.colors.textOnPageBackground, fontSize: '14px', marginTop: '4px' },
  scopeChip: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 20, color: '#3b82f6', fontSize: 13, textTransform: 'capitalize' },
  scopeClearBtn: { background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', display: 'flex', padding: 0 },
  tabRow:    { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tabBtn:    { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 6, border: 'none', background: '#1e293b', color: '#94a3b8', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  tabBtnActive: { background: '#6366f1', color: '#fff' },
  errorBox:  { padding: '12px 16px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', marginBottom: 16 },
  list:      { display: 'flex', flexDirection: 'column', gap: 12 },
};
